import React, {useState, useCallback} from 'react';
import ReactDOM from 'react-dom';
import {AppText} from './Typography';
import AppTableHeaderCell from './AppTableHeaderCell';
import AppActionsMenu from './AppActionsMenu';
import TablePagination from './TablePagination';
import {TableLoadingState} from '../loading/TableLoadingState';


export interface TableColumn<T> {
  key: string;
  title: React.ReactNode;
  className?: string;
  render: (row: T) => React.ReactNode;
  sortable?: boolean;
}

export interface ActionItem<T = unknown> {
  label: string;
  icon?: React.ReactNode;
  onClick: (row: T) => void;
  danger?: boolean;
}

interface AppTableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  rowKey: (row: T, index?: number) => React.Key;
  emptyText?: string;
  className?: string;
  showImage?: boolean;
  getImageUrl?: (row: T) => string | null;
  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  actions?: (row: T) => ActionItem[];
  hidePagination?: boolean;
  onRowClick?: (row: T) => void;
  showPageSizeSelector?: boolean;
  showPaginationInfo?: boolean;
  loading?: boolean;
  loadingRows?: number;
  loadingMessage?: string;
}

const PLACEHOLDER_IMG = '/assets/images/store_placeholder.png';

function AppTable<T> ({
  columns,
  data,
  rowKey,
  emptyText = 'لا توجد بيانات',
  className = '',
  showImage = false,
  getImageUrl,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  actions,
  hidePagination = false,
  onRowClick,
  showPageSizeSelector = true,
  showPaginationInfo = true,
  loading = false,
  loadingRows = 5,
  loadingMessage = 'جاري تحميل البيانات...'
}: AppTableProps<T>) {


  const [openActionRow, setOpenActionRow] = useState<React.Key | null>(null);
  const [menuPosition, setMenuPosition] = useState<{top: number; left: number} | null>(null);
  const MENU_HEIGHT = 220; // approx max height of the action menu

  const handleActionButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>, rowKey: React.Key) => {
    e.stopPropagation();
    if (openActionRow === rowKey) {
      setOpenActionRow(null);
      setMenuPosition(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const MENU_WIDTH = 280;
    const MARGIN = 8;

    // Vertical: open downward, flip upward if not enough space
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < MENU_HEIGHT
      ? rect.top + window.scrollY - MENU_HEIGHT - 4   // open upward
      : rect.bottom + window.scrollY + 4;              // open downward

    // Horizontal: align right edge of menu with right edge of button, clamp to viewport
    let left = rect.right + window.scrollX - MENU_WIDTH;
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - MENU_WIDTH - MARGIN));

    setMenuPosition({top, left});
    setOpenActionRow(rowKey);
  }, [openActionRow]);

  // ترتيب الأعمدة: [...columns, إجراءات, صورة]
  const finalColumns: TableColumn<T>[] = [
    ...(columns || []),
    ...(actions
      ? [
        {
          key: '__actions',
          title: 'إجراءات',
          className: 'w-12',
          render: (row: T) => (
            <div className="flex justify-center items-center">
              <button
                className="p-1 rounded-full hover:bg-brand-50"
                onClick={(e) => handleActionButtonClick(e, rowKey?.(row))}
                aria-label="خيارات"
              >
                <span className="text-gray-400 text-xl">•••</span>
              </button>
              {openActionRow === rowKey?.(row) && menuPosition && ReactDOM.createPortal(
                <AppActionsMenu
                  items={(actions?.(row) || []).map((a) => ({
                    label: a.label,
                    icon: a.icon,
                    onClick: () => {
                      a.onClick(row);
                      setTimeout(() => { setOpenActionRow(null); setMenuPosition(null); }, 100);
                    },
                    className: a.danger ? 'text-red-600' : ''
                  }))}
                  onClose={() => { setOpenActionRow(null); setMenuPosition(null); }}
                  style={{position: 'absolute', top: menuPosition.top, left: menuPosition.left, zIndex: 9999}}
                />,
                document.body
              )}
            </div>
          )
        }
      ]
      : []),
    ...(showImage
      ? [
        {
          key: '__image',
          title: '',
          className: 'w-20',
          render: (row: T) => (
            <img
              src={
                getImageUrl
                  ? getImageUrl?.(row) || PLACEHOLDER_IMG
                  : PLACEHOLDER_IMG
              }
              alt="img"
              className="w-12 h-12 rounded-lg object-cover mx-auto"
            />
          )
        }
      ]
      : [])
  ];

  const totalPages =
    pageSize && totalCount ? Math.ceil(totalCount / pageSize) : 1;


  return (
    <div
      className={`w-full rounded-xl shadow bg-white mt-5 overflow-hidden ${className}`}
      dir="rtl"
    >
      <div className="overflow-x-auto">
      <table
        className="w-full min-w-max text-right font-zain text-lg border-separate"
        style={{borderSpacing: '0 2px'}}
      >
        <thead>
          <tr style={{background: 'var(--color-table-header-bg, #f2f3f5)'}}>
            {(finalColumns || []).map((col) => {
              // If title is already an AppTableHeaderCell, render it directly
              if (col.title && React.isValidElement(col.title) && col.title.type === AppTableHeaderCell) {
                return React.cloneElement(col.title, {
                  key: col.key || `col-${Math.random()}`
                });
              }

              // Otherwise, use the standard AppTableHeaderCell
              return (
                <AppTableHeaderCell
                  key={col.key || `col-${Math.random()}`}
                  label={typeof col.title === 'string' ? col.title : undefined}
                >
                  {col.title && typeof col.title !== 'string' ? col.title : undefined}
                </AppTableHeaderCell>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {(() => {
            if (loading) {
              return (
                <tr>
                  <td
                    colSpan={finalColumns?.length || 0}
                    className="p-0"
                  >
                    <TableLoadingState
                      rows={loadingRows}
                      columns={finalColumns?.length || 6}
                      message={loadingMessage}
                    />
                  </td>
                </tr>
              );
            }
            if (!data || data.length === 0) {
              return (
                <tr>
                  <td
                    colSpan={finalColumns?.length || 0}
                    className="text-center py-8 text-grayUnit bg-white rounded-xl"
                  >
                    <AppText className="text-grayUnit">{emptyText}</AppText>
                  </td>
                </tr>
              );
            }
            return (data || []).map((row, index) => (
              <tr
                key={rowKey?.(row, index) || `row-${index}`}
                className={`bg-white rounded-xl transition mb-1 shadow-none border-none ${
                  onRowClick ? 'cursor-pointer' : ''
                }`}
                style={{height: 44}}
                onMouseEnter={onRowClick ? (e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-table-row-hover, #f9fafb)'; } : undefined}
                onMouseLeave={onRowClick ? (e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#fff'; } : undefined}
                onClick={() => onRowClick?.(row)}
              >
                {(finalColumns || []).map((col) => (
                  <td
                    key={col.key || `col-${Math.random()}`}
                    className={`px-4 py-3 text-right border-none bg-transparent align-middle ${
                      col.className || ''
                    }`}
                  >
                    {typeof col.render?.(row) === 'string' ||
                    typeof col.render?.(row) === 'number' ? (
                        <AppText>{col.render?.(row)}</AppText>
                      ) : (
                        col.render?.(row)
                      )}
                  </td>
                ))}
              </tr>
            ));
          })()}
        </tbody>
      </table>
      </div>

      {/* Enhanced Pagination - Always show if pagination is enabled and we have data */}
      {!hidePagination && page && pageSize != null && totalCount && onPageChange && totalCount > 0 && (
        <TablePagination
          currentPage={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange || (() => {})}
          showPageSizeSelector={showPageSizeSelector}
          showInfo={showPaginationInfo}
        />
      )}
    </div>
  );
}

export default AppTable;
